// src/generation/expected-trace.js
//
// B-5: 正常系 expectedTrace を「人間が編集したタイミングチャート」から導出する。
// フローの IF/THEN 分岐は実行・シミュレートしない（ST と同じ論理から再導出すると自己採点になるため）。
// 使うのは flow の motion ノードが持つ静的メタデータ（どの actuator/action か）と、
// project.actuators[].actions[].command/doneConditions（どの信号が動くか）、
// そして timing-state.js の recomputeAll が算出する開始/終了秒（いつ動くか）だけ。

import { recomputeAll, findActuatorAction } from "../timing/timing-state.js";
import { getNodeData, getNodeTypeFromData } from "../flow/flow-node-html.js";

// センサ待ちの doneCondition は物理プラント/実機のスキャン遅延分だけ実測がずれるため許容幅を持たせる。
// setOutputs/resetOutputs はSTがそのスキャンで即時に出す指令なので許容なし（厳密一致）のまま。
// 数値は fa-agent の異常系 judge (abnormal_trace.py の detect_tolerance_ms) と揃えている。
const DONE_CONDITION_TOLERANCE_MS = 50;

function getHomeFlowData(project) {
    return project?.flow?.drawflowData?.drawflow?.Home?.data || {};
}

function isDiscreteIoMotionNode(data) {
    return getNodeTypeFromData(data) === "motion" && !!data.actuatorId && !!data.actionId;
}

// project: 単一の project JSON（axes / actuators / signals / flow を持つ）
// 戻り値: { trace: [{t_ms, signal, value}], warnings: string[] }
export function buildExpectedTraceFromProject(project) {
    const homeData = getHomeFlowData(project);
    const computed = recomputeAll({ project, flowDataSnapshot: homeData });

    const trace = [];
    const warnings = [];

    for (const node of Object.values(homeData)) {
        const data = getNodeData(node);
        if (!isDiscreteIoMotionNode(data)) continue;

        const nodeId = String(node.id);
        const times = computed.actuatorMotionTimes[nodeId];
        if (!times) {
            warnings.push(`動作ノード「${data.label || nodeId}」のタイミングを計算できませんでした`);
            continue;
        }

        const { action } = findActuatorAction(data.actuatorId, data.actionId, project);
        if (!action) {
            warnings.push(`動作ノード「${data.label || nodeId}」が参照するアクチュエータ/アクションが見つかりません`);
            continue;
        }

        const startMs = Math.round(times.startSec * 1000);
        const endMs = Math.round(times.endSec * 1000);

        for (const signalId of action.command?.setOutputs || []) {
            trace.push({ t_ms: startMs, signal: signalId, value: 1 });
        }
        for (const signalId of action.command?.resetOutputs || []) {
            trace.push({ t_ms: startMs, signal: signalId, value: 0 });
        }
        for (const condition of action.doneConditions || []) {
            trace.push({
                t_ms: endMs,
                signal: condition.signalId,
                value: condition.value === false ? 0 : 1,
                tolerance_ms: DONE_CONDITION_TOLERANCE_MS
            });
        }
    }

    trace.sort((a, b) => a.t_ms - b.t_ms);

    return { trace, warnings };
}
